import { Link } from '@tanstack/react-router';
import { CopyCheckIcon, CopyIcon, DownloadIcon, ExternalLinkIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import useDownloader from 'react-use-downloader';
import { useBreakpointBelow } from '~/hooks/use-breakpoints';
import { useCopyToClipboard } from '~/hooks/use-copy-to-clipboard';
import { Spinner } from '~/modules/common/spinner';
import { toaster } from '~/modules/common/toaster/toaster';
import { TooltipButton } from '~/modules/common/tooltip-button';
import { Button } from '~/modules/ui/button';
import { ButtonGroup } from '~/modules/ui/button-group';
import { useSheeter } from '../common/sheeter/use-sheeter';

interface JsonActionsProps {
  url: string;
  data: unknown;
  filename?: string;
  resourceName?: string;
  className?: string;
  smallMode?: boolean;
  viewerUrl?: string;
}

export function JsonActions({ url, data, filename = 'data.json', resourceName, className, smallMode, viewerUrl }: JsonActionsProps) {
  const { t } = useTranslation();
  const isMobile = useBreakpointBelow('sm', false);

  const { copyToClipboard, copied } = useCopyToClipboard();
  const { download, isInProgress } = useDownloader();

  const handleCopy = () => {
    copyToClipboard(JSON.stringify(data, null, 2));
    toaster.success(t('c:success.resource_copied', { resource: resourceName ?? 'JSON' }));
  };

  const handleDownload = () => {
    if (!isInProgress) {
      download(url, filename);
    }
  };

  const handleOpen = () => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const size = smallMode ? 'xs' : 'default';

  return (
    <ButtonGroup className={className}>
      {viewerUrl && (
        <TooltipButton toolTipContent={t('c:view')}>
          <Button
            variant="outline"
            size={size}
            className="flex-none gap-2"
            render={
              <Link
                resetScroll
                to={viewerUrl}
                onClick={() => {
                  isMobile && useSheeter.getState().remove();
                  // Scroll the docs main content area to top (resetScroll only handles window scroll)
                  requestAnimationFrame(() => document.querySelector('main')?.scrollTo({ top: 0 }));
                }}
              />
            }
          >
            <span className={smallMode ? 'text-xs' : undefined}>{filename}</span>
          </Button>
        </TooltipButton>
      )}
      <TooltipButton toolTipContent={t('c:open')}>
        <Button variant="outline" size={size} className="flex-none gap-2" aria-label={t('c:open')} onClick={handleOpen}>
          <ExternalLinkIcon />
          {!smallMode && <span className="max-lg:hidden">{t('c:open')}</span>}
        </Button>
      </TooltipButton>
      <TooltipButton toolTipContent={t('c:copy')}>
        <Button variant="outline" size={size} className="gap-2" aria-label={t('c:copy')} onClick={handleCopy}>
          {copied ? <CopyCheckIcon /> : <CopyIcon />}
          {!smallMode && <span className="max-lg:hidden">{t('c:copy')}</span>}
        </Button>
      </TooltipButton>
      <TooltipButton toolTipContent={t('c:download')}>
        <Button
          variant="outline"
          size={size}
          className="flex-none gap-2"
          aria-label={t('c:download')}
          disabled={isInProgress}
          onClick={handleDownload}
        >
          {isInProgress ? <Spinner className="size-4" noDelay /> : <DownloadIcon />}
          {!smallMode && <span className="max-lg:hidden">{t('c:download')}</span>}
        </Button>
      </TooltipButton>
    </ButtonGroup>
  );
}
